<template>
  <div class="row q-mb-md" v-if="settingDialog == false">
    <q-card flat bordered class="col-12 q-pa-md">
      <span
        class="text-subpagetitle1 text-bold"
        :class="$q.dark.mode ? '' : 'text-primary '"
        >{{ pageTitle }}</span
      >
      <div>
        <q-toggle
          v-model="masterNotification.value"
          icon="account_box"
          :label="masterNotification?.label"
          @click="notificationSettingsData.master = masterNotification.value"
        >
          <q-tooltip class="bg-primary">
            {{ masterNotification.tooltip_description }}
          </q-tooltip>
        </q-toggle>
      </div>
      <q-separator />

      <!-- CUSTOM NOTIFICATION -->
      <div class="q-pa-sm q-pl-md">
        <p
          class="q-pl-sm text-bold"
          :class="$q.dark.mode ? '' : 'text-primary '"
        >
          Custom Notification
        </p>

        <div class="q-pl-md">
          <q-toggle
            v-for="(item, key) in customNotification"
            :key="key"
            v-model="item.value"
            :icon="item.icon || 'default_icon'"
            :label="item.label"
            :disable="!masterNotification.value"
            @click="notificationSettingsData[item.name] = item.value"
          >
            <q-tooltip class="bg-primary">
              {{ item.tooltip_description }}
            </q-tooltip>
          </q-toggle>
        </div>
      </div>
      <q-separator />

      <!-- GENERAL NOTIFICATION -->

      <div class="q-pa-sm q-pl-md">
        <p
          class="q-pl-sm text-bold"
          :class="$q.dark.mode ? '' : 'text-primary '"
        >
          General Notification
        </p>

        <div class="q-pl-md">
          <q-toggle
            v-for="(item, key) in generalNotifications"
            :key="key"
            v-model="item.value"
            :icon="item.icon || 'default_icon'"
            :label="item.label"
            :disable="!masterNotification.value"
            @click="notificationSettingsData[item.name] = item.value"
          >
            <q-tooltip class="bg-primary">
              {{ item.tooltip_description }}
            </q-tooltip>
          </q-toggle>
        </div>
      </div>

      <!-- MY SIGNAL NOTIFICATION -->
      <q-separator />
      <div class="q-pa-sm q-pl-md">
        <p
          class="q-pl-sm text-bold"
          :class="$q.dark.mode ? '' : 'text-primary '"
        >
          My Signal Notification

          <span
            class="text-caption"
            :class="$q.dark.mode ? 'text-white' : 'text-black '"
            >(When this is enabled, you will only be notified (for Stop
            Loss/Profit Target and Green/Red Signals) of those stocks that are
            added in 'My Signals')</span
          >
        </p>

        <div class="q-pl-md">
          <q-toggle
            v-for="(toggle, key) in mySignalNotification"
            :key="key"
            v-model="toggle.value"
            :icon="toggle.icon || 'default_icon'"
            :label="toggle.label"
            :disable="!masterNotification.value ||
              (!toggle.value && $store?.state?.packages?.packageStatus?.current_package?.stocks?.length == 0)"
            @click="notificationSettingsData[toggle.name] = toggle.value"
          >
            <q-tooltip class="bg-primary">
              {{ toggle.tooltip_description }}
            </q-tooltip>
          </q-toggle>
        </div>
      </div>
      <q-separator />

      <!-- SAVE -->
      <q-card-actions class="q-pa-md" align="center">
        <q-btn
          dense
          :color="$q.dark.mode ? 'grey-7' : 'primary'"
          align="center"
          push
          label="Save"
          @click="onClickSave"
          class="text-capitalize text-white"
          style="min-width: 130px"
        ></q-btn>
      </q-card-actions>
    </q-card>
  </div>
  <q-dialog v-model="warningDialog" square>
    <q-card>
      <q-bar class="bg-primary text-white">
        Alert !
        <q-space />
        <q-btn v-close-popup text-color="white" dense size="sm" icon="close">
          <q-tooltip class="bg-white text-primary">Close</q-tooltip>
        </q-btn>
      </q-bar>
      <div class="q-pa-sm">
        <div class="q-pa-md">
          <div class="q-pa-md">
            While Custom Stocks is on, you will only be notified of those stocks
            that are added in
            <span
              class="text-primary"
              style="text-decoration: underline; cursor: pointer"
              @click="$router.push({ path: '/my-signals' })"
            >
              'My Signals'.
            </span>
          </div>
        </div>
        <div style="display: flex; justify-content: end">
          <div>
            <q-btn color="primary" @click="submit()">Ok</q-btn>
          </div>
        </div>
      </div>
    </q-card>
  </q-dialog>
</template>

<script setup>
import { ref } from "vue";
import { useQuasar } from "quasar";
import { useStore } from "vuex";

const pageTitle = ref("Notification/Email/SMS Setting");
const emit = defineEmits(["notificationData"]);
const $q = useQuasar();
let warningDialog = ref(false);
const $store = useStore();

const props = defineProps({
  notificationData: {
    type: Object,
    default: () => {},
  },
  settingDialog: {
    type: Boolean,
    default: false,
  },
  customStocks: {
    type: Boolean,
  },
});

const notificationSettingsData = ref(props.notificationData);

const masterNotification = ref({
  name: "master",
  icon: "account_box",
  label: "All Sms and Notifications",
  tooltip_description: "SMS for Signal",
  value: notificationSettingsData.value.master,
});

const customNotification = ref([
  {
    name: "signal_sms",
    icon: "sms",
    label: "Signal Sms",
    tooltip_description: "SMS for Signal",
    value: notificationSettingsData.value.signal_sms,
  },
  {
    name: "signal_email",
    icon: "mail",
    label: "Signal Email",
    tooltip_description: "Email for Signal",
    value: notificationSettingsData.value.signal_email,
  },
  {
    name: "signal_notification",
    icon: "notifications",
    label: "Signal Notification",
    tooltip_description: "Notification for Signal",
    value: notificationSettingsData.value.signal_notification,
  },
  {
    name: "custom_target",
    icon: "mode_standby",
    label: "Custom Target",
    tooltip_description: "Notification for Custom Target",
    value: notificationSettingsData.value.custom_target,
  },
  {
    name: "stop_loss_profit_target",
    icon: "mode_standby",
    label: "Stop Loss Profit Target",
    tooltip_description: "Notification for Stop Loss Target",
    value: notificationSettingsData.value.stop_loss_profit_target,
  },
  {
    name: "circuit_setup",
    icon: "mode_standby",
    label: "Circuit Setup",
    tooltip_description: "Notification for Circuit Setup",
    value: notificationSettingsData.value.circuit_setup,
  },
  {
    name: "bulk_transaction",
    icon: "backup_table",
    label: "Bulk Transaction Individual",
    tooltip_description: "Notification for Bulk Transaction Individual",
    value: notificationSettingsData.value.bulk_transaction,
  },
  {
    name: "bulk_transaction_mkt_opt",
    icon: "queue",
    label: "Bulk Transaction (Mkt Opt)",
    tooltip_description: "Notification for Bulk Transaction Market Option",
    value: notificationSettingsData.value.bulk_transaction_mkt_opt,
  },
  // {
  //   name: "bulk_txn_sms",
  //   icon: "queue",
  //   label: "Bulk Transaction SMS (Mkt Opt)",
  //   tooltip_description: "SMS for Bulk Transaction Market Option",
  //   value: notificationSettingsData.value.bulk_txn_sms,
  // },
  // {
  //   name: "bulk_txm_email",
  //   icon: "queue",
  //   label: "Bulk Transaction Email (Mkt Opt)",
  //   tooltip_description: "Email for Bulk Transaction Market Option",
  //   value: notificationSettingsData.value.bulk_txm_email,
  // },
  {
    name: "bulk_txn_notification",
    icon: "queue",
    label: "Bulk Transaction With Strength (Mkt Opt) (Notification)",
    tooltip_description:
      "Notification for Bulk Transaction Market Option With Strength(Strong, Very Strong, Very Very Strong)",
    value: notificationSettingsData.value.bulk_txn_notification,
  },
  {
    name: "bulk_txn_sms_strong",
    icon: "sms",
    label: "Bulk Transaction Strong Sms (Mkt Opt)",
    tooltip_description: "Sms for Bulk Transaction Market Strong Option",
    value: notificationSettingsData.value.bulk_txn_sms_strong,
  },
  {
    name: "bulk_txn_sms_very_strong",
    icon: "sms",
    label: "Bulk Transaction Very Strong Sms (Mkt Opt)",
    tooltip_description: "Sms for Bulk Transaction Market Very Strong Option",
    value: notificationSettingsData.value.bulk_txn_sms_very_strong,
  },
  {
    name: "bulk_txn_sms_very_very_strong",
    icon: "sms",
    label: "Bulk Transaction Very Very Strong Sms (Mkt Opt)",
    tooltip_description:
      "Sms for Bulk Transaction Market Very Very Strong Option",
    value: notificationSettingsData.value.bulk_txn_sms_very_very_strong,
  },
]);

const generalNotifications = ref([
  {
    name: "offer_email",
    icon: "email",
    label: "Offer Email",
    tooltip_description: "Email for offers ",
    value: notificationSettingsData.value.offer_email,
  },
  {
    name: "general_notifications",
    icon: "notifications_active",
    label: "General Notifications",
    tooltip_description: "General Notifications",
    value: notificationSettingsData.value.general_notifications,
  },
]);

const mySignalNotification = ref([
  {
    name: "custom_stocks",
    icon: "notifications_active",
    label: "Custom Stocks",
    tooltip_description: "Notifications for my signals",
    value: notificationSettingsData.value.custom_stocks,
  },
]);

const submit = () => {
  warningDialog.value = false;
  emit("save", notificationSettingsData.value);
};
const onClickSave = () => {
  if (!props.customStocks && notificationSettingsData.value.custom_stocks) {
    warningDialog.value = true;
  } else {
    emit("save", notificationSettingsData.value);
  }
};
</script>
